import { Capacitor } from "@capacitor/core"
import posthog from "posthog-js"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { authClient } from "~/lib/auth-client"
import {
	consumePendingNativeAuth,
	setNativeAuthToken
} from "~/lib/native-auth-token"
import { posthogEnabled } from "./Posthog"
import { useTheme } from "./Theme"

export function NativeAppCapabilities() {
	const navigate = useNavigate()
	const { colorScheme } = useTheme()

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return

		document.documentElement.classList.add("orbit-native")

		const configureNativeApp = async () => {
			const { StatusBar, Style } = await import("@capacitor/status-bar")

			await StatusBar.show()
			await StatusBar.setOverlaysWebView({ overlay: true })
			await StatusBar.setStyle({
				style: colorScheme === "dark" ? Style.Dark : Style.Light
			})
			const info = await StatusBar.getInfo()
			document.documentElement.style.setProperty(
				"--orbit-status-bar-height",
				`${info.height}px`
			)
		}

		configureNativeApp().catch((error) => {
			console.error("Failed to configure native app", error)
		})

		return () => document.documentElement.classList.remove("orbit-native")
	}, [colorScheme])

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return

		let disposed = false
		const setup = async () => {
			const [{ App }, { Browser }] = await Promise.all([
				import("@capacitor/app"),
				import("@capacitor/browser")
			])
			const openUrl = async (url: string) => {
				const callback = new URL(url)

				if (callback.protocol === "orbit:" && callback.hostname === "auth") {
					// Only accept auth callbacks the app itself initiated: the link
					// must echo the pending login state armed at sign-in start and
					// carry a one-time claim code instead of a raw token, so
					// unsolicited or replayed links never replace the bearer identity.
					const state = callback.searchParams.get("state")
					if (!state || !consumePendingNativeAuth(state)) return

					const redirect = callback.searchParams.get("redirect") || "/"
					const code = callback.searchParams.get("code")
					let claimedToken: string | undefined
					if (code) {
						const claimed = await authClient.$fetch<{ token: string }>(
							"/api/native-auth/claim",
							{ query: { code } }
						)
						if (!claimed.error && typeof claimed.data?.token === "string")
							claimedToken = claimed.data.token
					}
					if (claimedToken) setNativeAuthToken(claimedToken)
					await Browser.close()
					navigate(redirect)
					if (claimedToken) window.location.reload()
					return
				}

				if (
					callback.protocol === "https:" &&
					callback.hostname === "orbit.buape.com"
				) {
					if (
						callback.pathname === "/s" ||
						callback.pathname.startsWith("/s/")
					) {
						await Browser.open({ url })
						return
					}
					navigate(
						`${callback.pathname}${callback.search}${callback.hash}` || "/"
					)
				}
			}
			const handle = await App.addListener("appUrlOpen", ({ url }) => {
				openUrl(url).catch((error) => {
					console.error("Failed to handle native app URL", error)
				})
			})
			const launch = await App.getLaunchUrl()
			if (launch?.url) await openUrl(launch.url)
			if (disposed) await handle.remove()
			return handle
		}
		const handle = setup().catch((error) => {
			console.error("Failed to set up native auth callback handling", error)
			return null
		})

		return () => {
			disposed = true
			void handle.then((listener) => listener?.remove())
		}
	}, [navigate])

	useEffect(() => {
		if (!posthogEnabled || !Capacitor.isNativePlatform()) return

		const registerDeviceProperties = async () => {
			try {
				const { Device } = await import("@capacitor/device")
				const [device, id, language] = await Promise.all([
					Device.getInfo(),
					Device.getId(),
					Device.getLanguageTag().catch(() => null)
				])
				const properties = {
					native_platform: device.platform,
					native_operating_system: device.operatingSystem,
					native_os_version: device.osVersion,
					native_model: device.model,
					native_manufacturer: device.manufacturer,
					native_is_virtual: device.isVirtual,
					native_webview_version: device.webViewVersion,
					native_device_id: id.identifier,
					native_language: language?.value
				}

				posthog.register(properties)
				posthog.capture("native_device_detected", properties)
			} catch (error) {
				console.error("Failed to register native device analytics", error)
			}
		}

		registerDeviceProperties()
	}, [])

	return null
}
